import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

/**
 * Spec §3b Part 5 wording — must not be paraphrased. The legal weight of the
 * sign-off (clinical responsibility transfers, permanent record) is the whole
 * reason this confirmation step exists, so we render the sentence verbatim in
 * the body and lean on the styling (non-muted, regular weight) to keep it
 * readable rather than fading into the chrome.
 */
const ACKNOWLEDGEMENT_BODY =
  "By acknowledging this patient you are confirming you have received the handover and " +
  "accepted clinical responsibility for this patient. This is a permanent record that " +
  "cannot be altered.";

export interface AcknowledgementModalProps {
  patient: { fullName: string; bedNumber: string };
  /** Whether the modal is open. Controlled by the parent so the same Dialog
   *  state can be inspected from elsewhere (e.g. a future "you have unfinished
   *  handovers" guard). */
  open: boolean;
  /**
   * Mirrors Radix Dialog's onOpenChange — fires on every close path (Cancel
   * button, Esc, backdrop). The spec calls this `onCancel`; we use the React-
   * idiomatic name because Confirm's success path also goes through here, and
   * "cancel after success" reads wrongly. While submitting, Esc and backdrop
   * dismissal are suppressed inside the modal, so onOpenChange only ever fires
   * from the explicit Cancel button until Confirm settles.
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user clicks Confirm. The modal shows a spinner until this
   * settles; on resolve it closes via onOpenChange(false), on reject it
   * surfaces a toast and stays open so the user can retry. The acknowledge API
   * call itself lives in the parent (E7.2 / HAN-56).
   */
  onConfirm: () => Promise<void>;
}

/**
 * E7.1 — the legal-warning modal the incoming doctor sees before any
 * acknowledgement is written. Spec priority item 9: the sign-off carries
 * clinical responsibility, so the confirmation is loud and the wording is
 * spec-exact. Built on shadcn `<Dialog>` (Radix under the hood), which gives
 * us the focus trap, the ARIA labelling, and the Esc-to-close behaviour out
 * of the box.
 */
export function AcknowledgementModal({
  patient,
  open,
  onOpenChange,
  onConfirm,
}: AcknowledgementModalProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // The toast is the spec's surface for E7.2's failure cases — session
      // locked, patient already acknowledged, network. The modal stays open
      // so the user can retry without losing context (which patient, which
      // session). The parent owns the API call; any tighter copy lives there.
      toast.error("Couldn't acknowledge this patient. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent
        // Block Esc and backdrop dismissal while the API call is in flight —
        // tearing the modal down mid-request would orphan the spinner and hide
        // any toast that lands afterwards. The Cancel/Confirm buttons are the
        // only paths out until submitting settles.
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Confirm handover acknowledgement</DialogTitle>
          <DialogDescription className="text-foreground">{ACKNOWLEDGEMENT_BODY}</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Patient: {patient.fullName} (Bed {patient.bedNumber})
        </p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            data-testid="acknowledgement-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Confirming…
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
